import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createTenantApiKey,
  getTenantApiKeys,
  getTenantApiKeyScopes,
  revokeTenantApiKey,
  TenantApiKey,
  updateTenantApiKey,
} from 'API/api-keys';
import { Roles } from 'enums';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { useUserData } from 'hooks/use-user-data';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import useUserStore from 'stores/user-store';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';

interface ApiKeyDialogState {
  open: boolean;
  loading: boolean;
  apiKey: TenantApiKey | null;
  name: string;
  rateLimit: string;
  expiresAt: string;
  scopes: string[];
  isActive: boolean;
}

const defaultDialogState: ApiKeyDialogState = {
  open: false,
  loading: false,
  apiKey: null,
  name: '',
  rateLimit: '60',
  expiresAt: '',
  scopes: ['*'],
  isActive: true,
};

function toDateTimeLocal(value: string | null | undefined) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

function copyToClipboard(text: string) {
  return navigator.clipboard.writeText(text).catch(() => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  });
}

const CompanyApiKeys: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { tenant, role } = useUserData();
  const details = useUserStore((state) => state.details);
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [keys, setKeys] = useState<TenantApiKey[]>([]);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState<ApiKeyDialogState>(defaultDialogState);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; loading: boolean; apiKey: TenantApiKey | null }>({
    open: false,
    loading: false,
    apiKey: null,
  });
  const [createdKey, setCreatedKey] = useState<{ open: boolean; value: string; name: string; copied: boolean }>({
    open: false,
    value: '',
    name: '',
    copied: false,
  });
  const [successAlert, setSuccessAlert] = useState({ open: false, message: '' });

  const tenantRole = useMemo(() => {
    return details?.tenants?.find((item) => item.tenantId === tenant)?.role?.toLowerCase() || details?.role?.toLowerCase() || '';
  }, [details, tenant]);

  const canManageApiKeys =
    tenantRole === Roles.ADMIN || tenantRole === Roles.OWNER || role === Roles.ADMIN || role === Roles.OWNER;

  const selectedScopes = dialogState.scopes;
  const isAllScopeSelected = selectedScopes.includes('*');
  const isDialogValid = dialogState.name.trim().length > 0 && Number(dialogState.rateLimit) > 0 && selectedScopes.length > 0;

  const loadApiKeyData = async () => {
    if (!canManageApiKeys) return;

    setLoading(true);
    const [keysRes, scopesRes] = await Promise.all([getTenantApiKeys(), getTenantApiKeyScopes()]);

    if (!('error' in keysRes)) {
      setKeys(keysRes.keys || []);
    } else {
      handleOpenErrorDialog(keysRes.error as any, '/api-keys', 'get');
    }

    if (!('error' in scopesRes)) {
      setAvailableScopes(Object.values(scopesRes.scopes || {}));
    } else {
      handleOpenErrorDialog(scopesRes.error as any, '/api-keys/scopes', 'get');
    }

    setLoading(false);
  };

  useEffect(() => {
    loadApiKeyData();
  }, [canManageApiKeys]);

  const openCreateDialog = () => {
    setDialogState({ ...defaultDialogState, open: true });
  };

  const openEditDialog = (apiKey: TenantApiKey) => () => {
    setDialogState({
      open: true,
      loading: false,
      apiKey,
      name: apiKey.name,
      rateLimit: String(apiKey.rateLimit || 60),
      expiresAt: toDateTimeLocal(apiKey.expiresAt),
      scopes: apiKey.scopes?.length ? apiKey.scopes : ['*'],
      isActive: apiKey.isActive,
    });
  };

  const closeDialog = () => {
    setDialogState(defaultDialogState);
  };

  const handleDialogFieldChange =
    (field: 'name' | 'rateLimit' | 'expiresAt') => (event: React.ChangeEvent<HTMLInputElement>) => {
      setDialogState((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleScopeToggle = (scope: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setDialogState((prev) => {
      if (scope === '*') {
        return { ...prev, scopes: checked ? ['*'] : [] };
      }

      const nextScopes = prev.scopes.filter((item) => item !== '*');
      if (checked) {
        return { ...prev, scopes: Array.from(new Set([...nextScopes, scope])) };
      }

      return { ...prev, scopes: nextScopes.filter((item) => item !== scope) };
    });
  };

  const handleActiveToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDialogState((prev) => ({ ...prev, isActive: event.target.checked }));
  };

  const handleSave = async () => {
    if (!isDialogValid) return;

    setDialogState((prev) => ({ ...prev, loading: true }));

    const payload = {
      name: dialogState.name.trim(),
      scopes: dialogState.scopes,
      rateLimit: Number(dialogState.rateLimit),
      expiresAt: dialogState.expiresAt ? new Date(dialogState.expiresAt).toISOString() : undefined,
      isActive: dialogState.apiKey ? dialogState.isActive : undefined,
    };

    if (dialogState.apiKey) {
      const res = await updateTenantApiKey(dialogState.apiKey._id, {
        name: payload.name,
        scopes: payload.scopes,
        rateLimit: payload.rateLimit,
        expiresAt: payload.expiresAt || null,
        isActive: payload.isActive,
      });

      if ('error' in res) {
        setDialogState((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res.error as any, '/api-keys/:id', 'patch');
        return;
      }

      setKeys((prev) => prev.map((item) => (item._id === res.key._id ? res.key : item)));
      setSuccessAlert({ open: true, message: t('apiKeyUpdatedSuccessfully', 'API key updated successfully') });
      closeDialog();
      return;
    }

    const res = await createTenantApiKey({
      name: payload.name,
      scopes: payload.scopes,
      rateLimit: payload.rateLimit,
      expiresAt: payload.expiresAt,
    });

    if ('error' in res) {
      setDialogState((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error as any, '/api-keys', 'post');
      return;
    }

    closeDialog();
    setCreatedKey({
      open: true,
      value: res.key,
      name: res.name,
      copied: false,
    });
    setSuccessAlert({ open: true, message: t('apiKeyCreatedSuccessfully', 'API key created successfully') });
    loadApiKeyData();
  };

  const openDeleteDialog = (apiKey: TenantApiKey) => () => {
    setDeleteDialog({ open: true, loading: false, apiKey });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, loading: false, apiKey: null });
  };

  const handleDelete = async () => {
    if (!deleteDialog.apiKey) return;

    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    const res = await revokeTenantApiKey(deleteDialog.apiKey._id);

    if ('error' in res) {
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error as any, '/api-keys/:id', 'delete');
      return;
    }

    setKeys((prev) => prev.map((item) => (item._id === deleteDialog.apiKey!._id ? { ...item, isActive: false } : item)));
    setSuccessAlert({ open: true, message: t('apiKeyRevokedSuccessfully', 'API key revoked successfully') });
    closeDeleteDialog();
  };

  const handleCopyCreatedKey = async () => {
    await copyToClipboard(createdKey.value);
    setCreatedKey((prev) => ({ ...prev, copied: true }));
    window.setTimeout(() => {
      setCreatedKey((prev) => ({ ...prev, copied: false }));
    }, 2000);
  };

  const closeCreatedKeyDialog = () => {
    setCreatedKey({ open: false, value: '', name: '', copied: false });
  };

  if (!canManageApiKeys) {
    return null;
  }

  return (
    <>
      <div className={classes.settingsBox}>
        <div className={classes.boxHeader}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <div>
              <Typography className={classes.boxTitle}>{t('companyApiKeys', 'API Keys')}</Typography>
              <Typography className={classes.helperText}>
                {t('companyApiKeysDescription', 'Generate and manage tenant API keys for external integrations and log access.')}
              </Typography>
            </div>
            <PaiButton
              text={t('createApiKey', 'Create API key')}
              border="rounded"
              size="small"
              action={openCreateDialog}
              startIcon={<KeyOutlinedIcon />}
            />
          </Stack>
        </div>
        <div className={classes.boxContent}>
          {loading ? (
            <Stack gap={2}>
              <Skeleton variant="rounded" height={120} />
              <Skeleton variant="rounded" height={120} />
            </Stack>
          ) : keys.length === 0 ? (
            <Typography className={classes.emptyState}>
              {t('noApiKeysCreatedYet', 'No API keys created yet. Create the first one here.')}
            </Typography>
          ) : (
            <Stack gap={2}>
              {keys.map((apiKey) => (
                <div key={apiKey._id} className={classes.keyCard}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography className={classes.keyName}>{apiKey.name}</Typography>
                        <Chip
                          size="small"
                          className={cx(classes.statusChip, apiKey.isActive ? 'active' : 'inactive')}
                          label={apiKey.isActive ? t('active') : t('inactive')}
                        />
                        <Chip size="small" variant="outlined" label={apiKey.keyPrefix} />
                      </Stack>
                      <Typography className={classes.metaText}>
                        {t('createdAt')}: {formatDateTime(apiKey.createdAt, 'Pp')}
                      </Typography>
                      <Typography className={classes.metaText}>
                        {t('apiKeyRateLimit', 'Rate limit')}: {apiKey.rateLimit}/{t('minute', 'minute')}
                      </Typography>
                      <Typography className={classes.metaText}>
                        {t('lastUsed', 'Last used')}: {apiKey.lastUsedAt ? formatDateTime(apiKey.lastUsedAt, 'Pp') : '-'}
                      </Typography>
                      <Typography className={classes.metaText}>
                        {t('expiresAt')}: {apiKey.expiresAt ? formatDateTime(apiKey.expiresAt, 'Pp') : t('never', 'Never')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="flex-end">
                      <PaiButton
                        text={t('edit', 'Edit')}
                        border="rounded"
                        size="extra-small"
                        action={openEditDialog(apiKey)}
                        startIcon={<EditOutlinedIcon />}
                      />
                      <PaiButton
                        text={t('revoke', 'Revoke')}
                        border="rounded"
                        size="extra-small"
                        color="error"
                        action={openDeleteDialog(apiKey)}
                        startIcon={<DeleteOutlineIcon />}
                        disabled={!apiKey.isActive}
                      />
                    </Stack>
                  </Stack>
                  <Divider className={classes.cardDivider} />
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {apiKey.scopes.map((scope) => (
                      <Chip key={scope} size="small" label={scope} variant="outlined" />
                    ))}
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </div>
      </div>

      <Dialog open={dialogState.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogState.apiKey ? t('editApiKey', 'Edit API key') : t('createApiKey', 'Create API key')}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} className={classes.dialogContent}>
            <PaiTextField
              label={t('name')}
              value={dialogState.name}
              onChange={handleDialogFieldChange('name')}
              placeholder={t('apiKeyNamePlaceholder', 'e.g. Log Database Access')}
            />
            <PaiTextField
              label={t('apiKeyRateLimit', 'Rate limit')}
              value={dialogState.rateLimit}
              onChange={handleDialogFieldChange('rateLimit')}
              type="number"
              min={1}
            />
            <div>
              <Typography className={classes.fieldLabel}>{t('expiresAt')}</Typography>
              <TextField
                fullWidth
                type="datetime-local"
                value={dialogState.expiresAt}
                onChange={handleDialogFieldChange('expiresAt')}
                size="small"
              />
            </div>
            {dialogState.apiKey ? (
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Typography className={classes.fieldLabel}>{t('active')}</Typography>
                <PaiSwitch checked={dialogState.isActive} onChange={handleActiveToggle} />
              </Stack>
            ) : null}
            <div>
              <Typography className={classes.fieldLabel}>{t('scopes', 'Scopes')}</Typography>
              <Stack className={classes.scopeList}>
                {availableScopes.map((scope) => (
                  <PaiCheckbox
                    key={scope}
                    checked={selectedScopes.includes(scope)}
                    label={scope}
                    onChange={handleScopeToggle(scope)}
                    disabled={scope !== '*' && isAllScopeSelected}
                  />
                ))}
              </Stack>
            </div>
          </Stack>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <PaiButton text={t('cancel')} size="small" border="rounded" color="secondary" action={closeDialog} />
          <PaiButton
            text={dialogState.apiKey ? t('save') : t('create', 'Create')}
            size="small"
            border="rounded"
            action={handleSave}
            loading={dialogState.loading}
            disabled={!isDialogValid}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('revokeApiKey', 'Revoke API key')}</DialogTitle>
        <DialogContent>
          <Typography className={classes.confirmationText}>
            {t('revokeApiKeyWarning', 'This API key will be disabled immediately and external clients using it will stop working.')}
          </Typography>
          <Typography className={classes.confirmationName}>{deleteDialog.apiKey?.name}</Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <PaiButton text={t('cancel')} size="small" border="rounded" color="secondary" action={closeDeleteDialog} />
          <PaiButton
            text={t('revoke', 'Revoke')}
            size="small"
            border="rounded"
            color="error"
            action={handleDelete}
            loading={deleteDialog.loading}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={createdKey.open} onClose={closeCreatedKeyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('newApiKeyCreated', 'New API key created')}</DialogTitle>
        <DialogContent>
          <Stack gap={2} className={classes.dialogContent}>
            <Typography className={classes.confirmationText}>
              {t('apiKeyShownOnce', 'Store this key now. For security reasons it is shown only once.')}
            </Typography>
            <PaiTextField label={t('name')} value={createdKey.name} readonly={true} />
            <TextField
              fullWidth
              value={createdKey.value}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyCreatedKey} edge="end">
                      <ContentCopyIcon color={createdKey.copied ? 'success' : 'inherit'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onClick={(event) => (event.target as HTMLInputElement).select?.()}
              size="small"
            />
            <Typography className={classes.copyHint}>
              {createdKey.copied ? t('copiedToClipboard', 'Copied to clipboard!') : t('copyApiKeyNow', 'Copy the key now and store it securely.')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <PaiButton text={t('close', 'Close')} size="small" border="rounded" action={closeCreatedKeyDialog} />
        </DialogActions>
      </Dialog>

      <PaiSuccessAlert
        open={successAlert.open}
        message={successAlert.message}
        position="bottom"
        handleClose={() => setSuccessAlert({ open: false, message: '' })}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  settingsBox: {
    background: '#FFF',
    borderRadius: '12px',
    padding: '28px 32px',
    [theme.breakpoints.down('lg')]: {
      padding: '24px',
    },
  },
  boxHeader: {
    paddingBottom: '20px',
  },
  boxTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 500,
  },
  helperText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    marginTop: '4px',
  },
  boxContent: {
    padding: 0,
  },
  emptyState: {
    color: theme.palette.text.secondary,
  },
  keyCard: {
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '8px',
    padding: '16px',
  },
  keyName: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
  },
  metaText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  cardDivider: {
    margin: '12px 0',
  },
  statusChip: {
    '&.active': {
      background: theme.palette.success.light,
      color: theme.palette.success.dark,
    },
    '&.inactive': {
      background: theme.palette.action.hover,
      color: theme.palette.text.secondary,
    },
  },
  dialogContent: {
    paddingTop: '8px',
  },
  dialogActions: {
    padding: '0 24px 20px 24px',
    gap: '12px',
  },
  fieldLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    marginBottom: '8px',
  },
  scopeList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    padding: '12px',
    gap: '8px',
    maxHeight: '240px',
    overflow: 'auto',
  },
  confirmationText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  confirmationName: {
    marginTop: '12px',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
  },
  copyHint: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.success.main,
  },
}));

export default CompanyApiKeys;
